import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, switchMap, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BreadcrumbService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PostingModule } from '../../../models/financial-account/financial-account';
import { JournalEntry, JournalEntryInsert, JournalEntryStatus } from '../../../models/journal-entry/journal-entry';
import { ManualJournalEntryForm, ManualJournalEntryLineForm } from '../../../models/journal-entry/journal-entry-form';
import { JournalEntryProxyService } from '../../../proxies/journal-entry.proxy.service';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';

/** How many lines a new entry opens with — the smallest posting there is, one debit against one credit. */
const INITIAL_LINE_COUNT = 2;

/** Two amounts agree when they agree to the cent; anything finer is float noise, not a real difference. */
const CENT = 0.005;

/**
 * A line must carry exactly one of debit or credit — which is what double entry means, and what the API
 * refuses a line for violating. Reported on the group rather than on either control, because the defect is
 * the pair: neither amount is wrong on its own.
 */
function debitXorCredit(group: AbstractControl): ValidationErrors | null {
  const debit = group.get('debitAmount')?.value ?? 0;
  const credit = group.get('creditAmount')?.value ?? 0;
  if (debit > 0 && credit > 0) return { bothSides: true };
  if (debit <= 0 && credit <= 0) return { noAmount: true };
  return null;
}

/**
 * Refuses the same GL account on more than one line.
 *
 * <p>Two lines on one account are not invalid double entry — two debits to the same account with different
 * memos post perfectly well — but they always collapse into a single line for the same money, and split
 * across rows they make the entry harder to read and to reconcile against. So the screen takes the
 * stricter line: one account, one row.</p>
 *
 * <p>Lives on the line rather than on the array so the offending ROW can show what is wrong. It reads its
 * siblings through `parent`, which is what makes {@link JournalEntryManualComponent.revalidateLines}
 * necessary — Angular re-runs the validators of what changed and of its ancestors, never of its siblings,
 * so the FIRST line would otherwise keep a stale clean status after the second one duplicated it.</p>
 */
function uniqueAccountAcrossLines(group: AbstractControl): ValidationErrors | null {
  const account = group.get('accountReference')?.value;
  if (!account) return null;

  const siblings = (group.parent as FormArray | null)?.controls;
  if (!siblings) return null;

  const uses = siblings.filter((line) => line.get('accountReference')?.value === account).length;
  return uses > 1 ? { duplicateAccount: true } : null;
}

/**
 * Manual journal entry (#5077) — the one screen that writes to the ledger directly rather than as a
 * side effect of a source document posting.
 *
 * <p><b>What the user gets to decide, and what they do not.</b> A manual entry has no source document, so
 * it has no document date of its own: the posting date is the only date, and the server takes it as both.
 * Entries are captured in dollars, so there is no currency field — what the entry is worth in the books is
 * settled at post time against the company's functional currency, not typed here. Site and cost centre are
 * not carried at all.</p>
 *
 * <p><b>Draft, then post.</b> Saving writes a Draft, which moves nothing; posting is what touches account
 * balances, and it is one-way — a posted entry is immutable, and a correction is a new entry, never an
 * edit of the one already in the ledger. That is why this screen refuses to open anything but a Draft, and
 * why it hands a posted entry over to the read-only details page instead.</p>
 */
@Component({
  selector: 'app-journal-entry-manual',
  templateUrl: './journal-entry-manual.component.html',
  styleUrl: './journal-entry-manual.component.scss',
})
export class JournalEntryManualComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(JournalEntryProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  headerTitle = signal<string>(this.t('JournalEntry_Manual_New', 'New journal entry'));

  /** The saved draft, once there is one. Null while the entry has never been written. */
  readonly entry = signal<JournalEntry | null>(null);

  /** Manual entries are captured in dollars (#5077); the server fixes this, the screen only states it. */
  readonly currencyLabel = 'USD';

  /** Only accounts the General Ledger may post to are offered — the same module the server posts this
   *  entry as, so the picker and the controlled-account guard agree on what is selectable. */
  readonly generalLedgerModule = PostingModule.GeneralLedger;

  readonly form = new FormGroup<ManualJournalEntryForm>({
    postingDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    description: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(250)]),
    reference: new FormControl<string | null>(null, [Validators.maxLength(100)]),
    lineItems: new FormArray<FormGroup<ManualJournalEntryLineForm>>([]),
  });

  /** F-08 — a label for every control, enforced at compile time. */
  readonly fieldLabels: Record<keyof ManualJournalEntryForm, string> = {
    postingDate: 'JournalEntry_Manual_Form_Field_PostingDate',
    description: 'JournalEntry_Manual_Form_Field_Description',
    reference: 'JournalEntry_Manual_Form_Field_Reference',
    lineItems: 'JournalEntry_Manual_Form_Field_LineItems',
  };

  /**
   * A FormArray is not something a signal can observe, so the two things the template needs from it are
   * tracked by hand — and separately, because they change at very different rates.
   *
   * <p>`structureVersion` moves when a line is added or removed; `amountsVersion` moves on every keystroke.
   * Rebuilding the table's row array off the second would re-render the whole table while somebody is
   * typing in it, which is how an input loses focus mid-number.</p>
   */
  private readonly structureVersion = signal(0);
  private readonly amountsVersion = signal(0);

  get lineItems(): FormArray<FormGroup<ManualJournalEntryLineForm>> {
    return this.form.controls.lineItems;
  }

  /**
   * The rows the table renders — a NEW array each time the set of lines changes.
   *
   * <p>p-table re-renders on a changed `[value]` reference, and pushing into a FormArray mutates its
   * `controls` array in place. Binding that array directly means an added line never appears.</p>
   */
  readonly lineRows = computed<FormGroup<ManualJournalEntryLineForm>[]>(() => {
    this.structureVersion();
    return [...this.lineItems.controls];
  });

  readonly totalDebits = computed(() => {
    this.amountsVersion();
    this.structureVersion();
    return this.lineItems.controls.reduce((sum, line) => sum + (line.controls.debitAmount.value ?? 0), 0);
  });

  readonly totalCredits = computed(() => {
    this.amountsVersion();
    this.structureVersion();
    return this.lineItems.controls.reduce((sum, line) => sum + (line.controls.creditAmount.value ?? 0), 0);
  });

  readonly difference = computed(() => this.totalDebits() - this.totalCredits());

  /**
   * Balanced means the two columns agree AND something was actually entered. An empty entry has debits and
   * credits of zero, which agree perfectly and post nothing — treating that as balanced would light the
   * green state on a blank form and offer to post it.
   */
  readonly isBalanced = computed(() => Math.abs(this.difference()) < CENT && this.totalDebits() > 0);

  /** Posting is one-way and touches balances, so it stays out of reach until the entry is one the server
   *  would accept: balanced, with a complete header and every line carrying an account and an amount. */
  readonly canPost = computed(() => {
    this.amountsVersion();
    this.structureVersion();
    return this.isBalanced() && this.form.valid && this.lineItems.length >= INITIAL_LINE_COUNT;
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.lineItems.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.revalidateLines();
      this.amountsVersion.update((version) => version + 1);
    });
  }

  /**
   * Re-runs every line's validators, because a duplicate account is a property of the PAIR: picking an
   * account on line 3 is what makes line 1 wrong, and Angular never revalidates a sibling on its own.
   *
   * <p>Silent throughout — an emitting update here would land straight back in the subscription that
   * called it. The array is refreshed at the end because its own status is derived from its children's,
   * and revalidating them with `onlySelf` deliberately leaves it stale.</p>
   */
  private revalidateLines(): void {
    for (const line of this.lineItems.controls) line.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    this.lineItems.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.load(id);
      return;
    }
    this.breadcrumbService.setCurrent('Common_New');
    for (let i = 0; i < INITIAL_LINE_COUNT; i++) this.addLine();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private toast(severity: 'success' | 'warn' | 'error', summaryKey: string, summary: string, detail: string): void {
    this.messageService.add({ key: 'br', severity, summary: this.t(summaryKey, summary), detail });
  }

  // ---- lines ----

  private buildLine(): FormGroup<ManualJournalEntryLineForm> {
    const group = new FormGroup<ManualJournalEntryLineForm>(
      {
        accountReference: new FormControl<string | null>(null, [Validators.required]),
        accountNumber: new FormControl<string | null>(null),
        accountName: new FormControl<string | null>(null),
        description: new FormControl<string | null>(null, [Validators.maxLength(250)]),
        debitAmount: new FormControl<number | null>(null, [Validators.min(0)]),
        creditAmount: new FormControl<number | null>(null, [Validators.min(0)]),
      },
      { validators: [debitXorCredit, uniqueAccountAcrossLines] },
    );

    // A line is a debit or a credit, never both — so entering one clears the other rather than letting the
    // user build a line the API refuses. Driven off the control rather than the input's own event, which
    // fires before the control has taken the value. The clear is silent, or the two chase each other.
    group.controls.debitAmount.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if ((value ?? 0) > 0 && group.controls.creditAmount.value) group.controls.creditAmount.setValue(null, { emitEvent: false });
    });
    group.controls.creditAmount.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if ((value ?? 0) > 0 && group.controls.debitAmount.value) group.controls.debitAmount.setValue(null, { emitEvent: false });
    });

    return group;
  }

  addLine(): void {
    this.lineItems.push(this.buildLine());
    this.structureVersion.update((version) => version + 1);
  }

  /**
   * Removes a line, never below the two an entry needs. The button is hidden at the floor rather than
   * disabled — there is nothing the user could do to make it work, so offering it would be a dead end.
   */
  removeLine(index: number): void {
    if (this.lineItems.length <= INITIAL_LINE_COUNT) return;
    this.lineItems.removeAt(index);
    this.structureVersion.update((version) => version + 1);
  }

  readonly canRemoveLine = computed(() => {
    this.structureVersion();
    return this.lineItems.length > INITIAL_LINE_COUNT;
  });

  /** Unwraps a p-table row back to its typed line group. */
  getLineGroup(row: unknown): FormGroup<ManualJournalEntryLineForm> {
    return row as FormGroup<ManualJournalEntryLineForm>;
  }

  // ---- load ----

  private load(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.apply(response?.data ?? null),
        error: () => undefined,
      });
  }

  /**
   * Fills the form from a saved draft.
   *
   * <p>Anything that is not an editable draft is handed to the read-only details page instead of being
   * shown in a form that cannot save it: a posted entry is immutable, and an entry from AP or AR is a
   * projection of its source document, which is where a correction belongs.</p>
   */
  private apply(entry: JournalEntry | null): void {
    if (!entry) return;

    if (entry.status !== JournalEntryStatus.Draft || entry.sourceModule !== PostingModule.GeneralLedger) {
      this.router.navigate(['/journal-entry-details'], { queryParams: { id: entry.id } });
      return;
    }

    this.entry.set(entry);
    this.headerTitle.set(entry.number);
    this.breadcrumbService.setCurrent(entry.number);

    this.form.patchValue(
      {
        postingDate: entry.postingDate ? new Date(entry.postingDate) : null,
        description: entry.description,
        reference: entry.reference ?? null,
      },
      { emitEvent: false },
    );

    this.lineItems.clear({ emitEvent: false });
    for (const line of entry.lineItems ?? []) {
      const group = this.buildLine();
      group.patchValue(
        {
          accountReference: line.accountReference,
          accountNumber: line.accountNumber,
          accountName: line.accountName,
          description: line.description ?? null,
          debitAmount: line.debitAmount || null,
          creditAmount: line.creditAmount || null,
        },
        { emitEvent: false },
      );
      this.lineItems.push(group, { emitEvent: false });
    }
    while (this.lineItems.length < INITIAL_LINE_COUNT) this.lineItems.push(this.buildLine(), { emitEvent: false });
    this.structureVersion.update((version) => version + 1);
    this.amountsVersion.update((version) => version + 1);
  }

  // ---- save / post ----

  /** The request body. Local dates are sent as a plain calendar day: the posting date decides the fiscal
   *  period, and a UTC shift can move an entry booked late on the 31st into the next month. */
  private buildPayload(): JournalEntryInsert {
    const postingDate = this.form.controls.postingDate.value!;
    return {
      postingDate: this.toLocalIsoDate(postingDate),
      description: this.form.controls.description.value!.trim(),
      reference: this.form.controls.reference.value?.trim() || null,
      lineItems: this.lineItems.controls.map((line) => ({
        accountReference: line.controls.accountReference.value!,
        debitAmount: line.controls.debitAmount.value ?? 0,
        creditAmount: line.controls.creditAmount.value ?? 0,
        description: line.controls.description.value?.trim() || null,
      })),
    };
  }

  private toLocalIsoDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The 1-based numbers of the lines missing an account or an amount. Numbered as the user sees them,
   *  not as array indices. Deliberately does NOT count a duplicate, which gets its own message. */
  private incompleteLineNumbers(): number[] {
    return this.lineItems.controls
      .map((line, index) => ({ line, number: index + 1 }))
      .filter(({ line }) => line.controls.accountReference.hasError('required') || line.hasError('noAmount') || line.hasError('bothSides'))
      .map(({ number }) => number);
  }

  /** The 1-based numbers of the lines sharing an account with another line — every one of them, not just
   *  the later ones, since the user has to see both to decide which to change. */
  private duplicateLineNumbers(): number[] {
    return this.lineItems.controls
      .map((line, index) => ({ line, number: index + 1 }))
      .filter(({ line }) => line.hasError('duplicateAccount'))
      .map(({ number }) => number);
  }

  /**
   * Checks everything the server would, so a rejection arrives as a message about the form rather than as
   * a failed request. Returns whether the entry may be written.
   */
  private validate(): boolean {
    // Named before the generic report, which can only say that "lines" is invalid — useless on a ten-line
    // entry where one line is missing its account.
    const incomplete = this.incompleteLineNumbers();
    if (incomplete.length) {
      this.form.markAllAsTouched();
      this.toast(
        'warn',
        'JournalEntry_Manual_IncompleteLines_Title',
        'Incomplete lines',
        this.t(
          'JournalEntry_Manual_IncompleteLines',
          'Every line needs an account and either a debit or a credit. Check line {lines}.',
        ).replace('{lines}', incomplete.join(', ')),
      );
      return false;
    }

    const duplicates = this.duplicateLineNumbers();
    if (duplicates.length) {
      this.form.markAllAsTouched();
      this.toast(
        'warn',
        'JournalEntry_Manual_DuplicateAccount_Title',
        'Repeated account',
        this.t(
          'JournalEntry_Manual_DuplicateAccount_Detail',
          'An account can only appear on one line. Combine line {lines} into a single amount.',
        ).replace('{lines}', duplicates.join(', ')),
      );
      return false;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translateService);
      return false;
    }

    if (!this.isBalanced()) {
      this.toast(
        'warn',
        'JournalEntry_Unbalanced',
        'Unbalanced',
        this.t('JournalEntry_Manual_MustBalance', 'Total debits must equal total credits before the entry can be saved.'),
      );
      return false;
    }
    return true;
  }

  save(): void {
    if (!this.validate()) return;

    const payload = this.buildPayload();
    const id = this.currentId();
    this.startLoading();

    const request$ = this.isNew() || !id ? this.service.create(payload) : this.service.updateDraft(id, payload);

    request$.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        this.applySaved(response?.data ?? null);
        this.toast('success', 'Saved', 'Saved', this.t('JournalEntry_Manual_Saved', 'The draft entry was saved.'));
      },
      error: () => undefined,
    });
  }

  /**
   * Saves whatever is on screen and posts it in one action.
   *
   * <p>Chained rather than offered as two buttons the user must press in order: the draft on the server has
   * to match the form before it is posted, and making that the user's job is how an entry gets posted with
   * an edit that was never saved.</p>
   */
  post(): void {
    if (!this.validate()) return;

    const payload = this.buildPayload();
    const id = this.currentId();
    this.startLoading();

    const saved$ = this.isNew() || !id ? this.service.create(payload) : this.service.updateDraft(id, payload);

    saved$
      .pipe(
        switchMap((response) => {
          const saved = response?.data ?? null;
          this.applySaved(saved);
          // A save that came back without an id is a failure the error branch will report — posting an
          // undefined id would turn it into a confusing 404 about an entry that was never written.
          if (!saved?.id) return throwError(() => new Error('The draft was not saved.'));
          return this.service.post(saved.id);
        }),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: (response) => {
          const posted = response?.data;
          this.toast(
            'success',
            'Posted',
            'Posted',
            this.t('JournalEntry_Manual_Posted', 'Entry {number} was posted.').replace('{number}', posted?.number ?? ''),
          );
          // Posted entries are immutable, so there is nothing left for this screen to do with it — the
          // details page is where it is read from now on.
          this.router.navigate(['/journal-entry-details'], { queryParams: { id: posted?.id ?? this.currentId() } });
        },
        error: () => undefined,
      });
  }

  /** After a create, the screen stops being "new": it now edits the draft the server just wrote. */
  private applySaved(entry: JournalEntry | null): void {
    if (!entry?.id) return;
    this.entry.set(entry);
    this.currentId.set(entry.id);
    this.isNew.set(false);
    this.headerTitle.set(entry.number);
    // Keep the URL in step, so a reload reopens the draft rather than a blank form.
    this.router.navigate([], { relativeTo: this.route, queryParams: { id: entry.id }, replaceUrl: true });
    this.breadcrumbService.setCurrent(entry.number);
  }

  back(): void {
    this.router.navigate(['/journal-entries']);
  }
}
